import { useCallback, useRef } from 'react';
import axios from 'axios';

import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import useDataFetcher from '~/components/useDataFetcher';
import type {
  MetabaseCardMetadata,
  MetabaseQuestionsListResponse,
  MetabaseQuestionsListResponseMetabaseQuestion,
} from '~/server_shared/openapi/data-contracts';
import { reportAxiosError } from '~/Utils';

// Type aliases for cleaner exports
export type MetabaseQuestion = MetabaseQuestionsListResponseMetabaseQuestion;
export type { MetabaseCardMetadata };

export const useMetabaseQuestions = () => {
  // @ts-expect-error TODO: useSysconfig hook needs TypeScript definition
  const { organization } = useSysconfig();
  // Use ref to cache column data (stable reference, no re-render dependency)
  const columnsCacheRef = useRef<Record<number, MetabaseCardMetadata>>({});

  const baseRoute = `/sysconfig/api/v1/organizations/${organization.id}/reports/metabase-questions`;

  const { isLoading: isLoadingQuestions, data: questionsData } =
    useDataFetcher<MetabaseQuestionsListResponse>(baseRoute);

  const questions = questionsData?.questions || [];

  /**
   * Fetch column metadata for a specific Metabase question.
   * Results are cached to avoid repeated API calls.
   * Uses ref for cache check to maintain stable function reference.
   */
  const getQuestionColumns = useCallback(
    async (questionId: number): Promise<MetabaseCardMetadata | null> => {
      // Check cache first using ref (stable reference, no re-render dependency)
      if (columnsCacheRef.current[questionId]) {
        return columnsCacheRef.current[questionId];
      }

      try {
        const { data } = await axios.get<MetabaseCardMetadata>(`${baseRoute}/${questionId}/columns`);
        columnsCacheRef.current[questionId] = data;
        return data;
      } catch (error) {
        await reportAxiosError(error);
        return null;
      }
    },
    [baseRoute]
  );

  return {
    questions,
    isLoadingQuestions,
    getQuestionColumns,
  };
};
